import {Column} from "primereact/column"
import {DataTable} from "primereact/datatable"
import {MultiSelect} from "primereact/multiselect"
import {useContext, useState} from "react"
import {Spinner} from "react-bootstrap"

import {wrappedPut} from "../../utilities/AxiosHelper.jsx"
import {bulkFilter} from "../../utilities/general"
import RefContext from "../../utilities/RefContext"

export function PreceptorTable({orientation, preceptors, onPreceptorsChange}) {
    const {dispatch} = useContext(RefContext)
    const [toAdd, setToAdd] = useState([])
    const [addSaving, setAddSaving] = useState(false)
    const [toRemove, setToRemove] = useState([])
    const [removeSaving, setRemoveSaving] = useState(false)
    const [nameSearch, setNameSearch] = useState("")
    const [assigned, setAssigned] = useState(orientation.preceptors ?? [])
    const [unitFilter, setUnitFilter] = useState([])
    const unitOptions = [...new Set(preceptors.map((p) => p.unit_name))].map((unit) => ({label: unit, value: unit}))
    const assignedIds = new Set(assigned.map((p) => p.preceptor_user_id))
    const availablePreceptors = bulkFilter(
        preceptors.filter((p) => !assignedIds.has(p.id)),
        [
            [["first_name", "last_name", "account"], nameSearch, "inexact"],
            ["unit_name", unitFilter, "includes"],
        ]
    )

    const showError = (error, fallback) => {
        const errorMessage = error.response?.data?.["Error"]
        dispatch("showToast", {
            show: true,
            variant: "danger",
            text: errorMessage || fallback,
            timeout: 5000,
        })
    }

    const assignSelected = () => {
        setAddSaving(true)
        wrappedPut(
            `/orientations/${orientation.id}/add-preceptors`,
            toAdd.map((p) => p.id)
        )
            .then((response) => {
                onPreceptorsChange(response.data.results)
                setAssigned(response.data.results)
                setToAdd([])
                dispatch("showToast", {
                    show: true,
                    variant: "success",
                    text: "Preceptors successfully assigned",
                    timeout: 5000,
                })
            })
            .catch((error) => showError(error, "An error occurred while adding preceptors."))
            .finally(() => {
                setAddSaving(false)
            })
    }

    const removeSelected = () => {
        setRemoveSaving(true)
        wrappedPut(
            `/orientations/${orientation.id}/remove-preceptors`,
            toRemove.map((p) => p.preceptorship_id)
        )
            .then((response) => {
                onPreceptorsChange(response.data.results)
                setAssigned(response.data.results)
                setToRemove([])
                dispatch("showToast", {
                    show: true,
                    variant: "success",
                    text: "Preceptors successfully removed",
                    timeout: 5000,
                })
            })
            .catch((error) => showError(error, "An error occurred while removing preceptors."))
            .finally(() => {
                setRemoveSaving(false)
            })
    }

    const ratingBody = (row) =>
        row.rating ? (
            <div className="d-flex align-items-center flex-column">
                <div className="d-flex justify-content-between w-100 mt-auto mb-2">
                    <span className="fw-semibold fs-7">Rating:</span>
                    <span className="fw-bold fs-7">{row.rating} / 4.0</span>
                </div>
                <div className="h-8px rounded mx-3 w-100 bg-secondary mb-3">
                    <div
                        className="bg-success rounded h-8px"
                        role="progressbar"
                        style={{width: `${(row.rating / 4) * 100}%`}}
                    ></div>
                </div>
            </div>
        ) : (
            "No Info"
        )

    const headerClass = "text-light text-uppercase fw-bold bg-gray-700 fs-7 align-middle"

    return (
        <>
            <div className="card mb-5">
                <div className="card-header border-0 flex-column flex-sm-row align-items-stretch align-items-sm-center gap-3">
                    <div className="card-title">
                        <h3>Assigned Preceptors</h3>
                    </div>
                    <div className="card-toolbar w-100 w-sm-auto">
                        <button
                            className="btn btn-flex btn-light-danger px-3 min-w-100px me-0 me-sm-4 w-100 w-sm-auto"
                            onClick={removeSelected}
                            disabled={removeSaving || toRemove.length === 0}
                        >
                            {removeSaving ? (
                                <>
                                    <span className="me-2">Please wait...</span>
                                    <Spinner as="span" animation="border" size="sm" aria-hidden="true" />
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-trash me-1"></i>Remove Selected
                                </>
                            )}
                        </button>
                    </div>
                </div>
                <div className="card-body pt-0 pb-5 table-responsive">
                    <DataTable
                        value={assigned}
                        selection={toRemove}
                        onSelectionChange={(e) => setToRemove(e.value)}
                        dataKey="preceptorship_id"
                        tableClassName="table align-middle table-rounded table-row-bordered border gs-5 gy-4"
                        rowHover={true}
                        onMouseDownCapture={(e) => {
                            e.stopPropagation()
                        }}
                        emptyMessage="No preceptors assigned to this orientation."
                    >
                        <Column headerClassName={headerClass} selectionMode="multiple" />
                        <Column
                            header="Preceptor Name"
                            headerClassName={headerClass}
                            body={(row) => row.preceptor_name}
                        />
                        <Column header="Unit" headerClassName={headerClass} body={(row) => row.unit_name} />
                        <Column
                            header="Date Added"
                            headerClassName={headerClass}
                            body={(row) => (row.assigned_at ? new Date(row.assigned_at).toLocaleDateString() : "")}
                        />
                        <Column
                            header="Orientee Rating"
                            headerClassName={`${headerClass} min-w-200px`}
                            body={ratingBody}
                        />
                    </DataTable>
                </div>
            </div>

            <div className="card">
                <div className="card-header border-0 flex-column flex-sm-row align-items-stretch align-items-sm-center gap-3">
                    <div className="card-title">
                        <h3>Available Preceptors</h3>
                    </div>
                    <div className="card-toolbar gap-3 gap-sm-4 flex-wrap w-100 w-sm-auto">
                        <div className="position-relative w-100 w-sm-auto">
                            <div className="d-flex align-items-center position-relative gap-3 w-100">
                                <i className="ki-outline ki-magnifier fs-3 position-absolute ms-4"></i>
                                <input
                                    type="text"
                                    className="form-control form-control-solid fw-bold ps-12 h-45px"
                                    placeholder="Preceptor Name"
                                    value={nameSearch}
                                    onChange={(evt) => {
                                        setNameSearch(evt.target.value)
                                    }}
                                />
                            </div>
                        </div>
                        <div className="position-relative w-100 w-sm-auto">
                            <div className="d-flex align-items-center position-relative gap-3 w-100">
                                <MultiSelect
                                    inputId="unit"
                                    value={unitFilter}
                                    options={unitOptions}
                                    onChange={(event) => setUnitFilter(event.value)}
                                    placeholder="All"
                                    display="chip"
                                    showClear
                                    className="w-100"
                                    ptOptions={{mergeProps: true}}
                                    pt={{
                                        token: {className: "bg-primary text-light"},
                                        removeTokenIcon: {className: "text-light"},
                                    }}
                                />
                            </div>
                        </div>
                        <button
                            className="btn btn-flex btn-light-primary px-3 min-w-100px me-0 me-sm-4 w-100 w-sm-auto"
                            onClick={assignSelected}
                            disabled={addSaving || toAdd.length === 0}
                        >
                            {addSaving ? (
                                <>
                                    <span className="me-2">Please wait...</span>
                                    <Spinner as="span" animation="border" size="sm" aria-hidden="true" />
                                </>
                            ) : (
                                <>
                                    <i className="ki-outline ki-plus-square fs-2"></i>Assign Selected
                                </>
                            )}
                        </button>
                    </div>
                </div>
                <div className="card-body pt-0 pb-5 table-responsive">
                    <DataTable
                        value={availablePreceptors}
                        selection={toAdd}
                        onSelectionChange={(e) => setToAdd(e.value)}
                        dataKey="id"
                        tableClassName="table align-middle table-rounded table-row-bordered border gs-5 gy-4"
                        rowHover={true}
                        onMouseDownCapture={(e) => {
                            e.stopPropagation()
                        }}
                        emptyMessage="No available preceptors."
                    >
                        <Column headerClassName={headerClass} selectionMode="multiple" />
                        <Column
                            header="Preceptor Name"
                            headerClassName={headerClass}
                            body={(row) => `${row.first_name} ${row.last_name}`}
                        />
                        <Column
                            header="# of Active Orientees"
                            headerClassName={headerClass}
                            body={(row) => row.active_orientation_count}
                        />
                        <Column header="Unit" headerClassName={headerClass} body={(row) => row.unit_name} />
                        <Column
                            header="Orientee Rating"
                            headerClassName={`${headerClass} min-w-200px`}
                            body={ratingBody}
                        />
                    </DataTable>
                </div>
            </div>
        </>
    )
}
